"use client";

import { useEffect, useMemo, useState } from "react";

import { createLumiApiClient } from "@/lib/lumi-api";
import {
  coverageBadgeLabel,
  coverageDisplay,
  getCachedCoverage,
  setCachedCoverage,
  type CoverageDisplay,
} from "./bookCoverage";

interface CoverageBadgeProps {
  bookId: string;
  className?: string;
}

/**
 * Quiet "96% known" pill for library book cards. Fetches per-book coverage
 * with a short client cache; renders nothing when data is unavailable.
 */
export default function CoverageBadge({
  bookId,
  className = "",
}: CoverageBadgeProps) {
  const api = useMemo(() => createLumiApiClient(), []);
  const [display, setDisplay] = useState<CoverageDisplay | null>(() =>
    coverageDisplay(getCachedCoverage(bookId)),
  );

  useEffect(() => {
    let cancelled = false;

    const cached = getCachedCoverage(bookId);
    if (cached) {
      setDisplay(coverageDisplay(cached));
      return;
    }

    setDisplay(null);

    api
      .getBookCoverage(bookId)
      .then((data) => {
        if (cancelled) return;
        setCachedCoverage(bookId, data);
        setDisplay(coverageDisplay(data));
      })
      .catch(() => {
        if (!cancelled) setDisplay(null);
      });

    return () => {
      cancelled = true;
    };
  }, [api, bookId]);

  if (!display) {
    return null;
  }

  return (
    <span
      className={`inline-flex items-center rounded-full border border-app-border bg-app-tinted px-2 py-0.5 text-[11px] font-medium tabular-nums text-app-body ${className}`}
      title={`${display.known} of ${display.total} lemmas known`}
    >
      {coverageBadgeLabel(display)}
    </span>
  );
}
